import { useState, useEffect, useRef } from 'react';

interface AreaSparkProps {
  color?: string;
  seed?: number;
  w?: number;
  h?: number;
  dir?: 1 | -1 | 0;
  active?: boolean;
  /**
   * Optional real-data series. When provided, the sparkline renders verbatim
   * from these values (rescaled linearly to the SVG coordinate space); when
   * absent, falls back to the deterministic seed-based synthetic curve.
   */
  data?: number[];
}

export function AreaSpark({
  color = 'var(--color-accent)',
  seed = 0,
  w = 120,
  h = 36,
  dir = 0,
  active = false,
  data,
}: AreaSparkProps) {
  const [pts, setPts] = useState<number[] | null>(null);
  const baseRef = useRef<number[] | null>(null);
  const perturbRef = useRef<number[] | null>(null);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef(0);

  // Generate base points — real data when provided, seed-based curve otherwise
  if (!baseRef.current) {
    let b: number[];
    if (data && data.length > 0) {
      const min = Math.min(...data);
      const max = Math.max(...data);
      const span = max - min || 1; // guard div-by-zero for flat series
      // Invert: higher value → lower y pixel (SVG y grows downward)
      b = data.map((v) => h - 4 - ((v - min) / span) * (h - 8));
    } else {
      b = [];
      let y = h * 0.5 + seed * 1.5;
      for (let i = 0; i <= 30; i++) {
        y +=
          Math.sin(i * 0.6 + seed) * 2.2 +
          Math.sin(i * 1.8 + seed * 2.7) * 1.1 +
          dir * 0.15;
        y = Math.max(6, Math.min(h - 4, y));
        b.push(y);
      }
    }
    baseRef.current = b;
  }
  const base = baseRef.current;

  useEffect(() => {
    if (active) {
      const perturb = base.map(() => (Math.random() - 0.5) * h * 0.55);
      perturbRef.current = perturb;
      startRef.current = performance.now();
      const animate = (now: number) => {
        const elapsed = now - startRef.current;
        const t = Math.min(elapsed / 700, 1);
        const ease = 1 - Math.pow(1 - t, 3);
        const current = base.map((by, i) =>
          Math.max(2, Math.min(h - 2, by + perturbRef.current![i] * (1 - ease))),
        );
        setPts(current);
        if (t < 1) rafRef.current = requestAnimationFrame(animate);
      };
      rafRef.current = requestAnimationFrame(animate);
    } else {
      setPts(null);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, base, h]);

  const ys = pts || base;
  const denom = (base.length - 1) || 1;
  const points = ys.map(
    (y, i) => `${((i / denom) * w).toFixed(1)},${y.toFixed(1)}`,
  );
  const line = points.join(' ');
  const area = `${line} ${w},${h} 0,${h}`;
  const id = `grad-${seed}-${color.replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop
            offset="0%"
            stopColor={color}
            stopOpacity={active ? 0.5 : 0.35}
          />
          <stop offset="100%" stopColor={color} stopOpacity={0.05} />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${id})`} />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth={active ? '2' : '1.8'}
        strokeLinecap="round"
        opacity="1"
        style={{ filter: `drop-shadow(0 0 2px ${color}33)` }}
      />
    </svg>
  );
}
